import { apiFetch, getAuthToken } from '../admin/jscontrol/apiClient';
import StorageManager from '../admin/jscontrol/StorageManager';
import { parseCartDetailResponse } from '../admin/cart/cartUtils';
import { parseUserIdFromToken } from './authHelpers';
import { confirmOrderFromCartApi } from './orderApi';
import { fetchBundleRelationCount } from './bundleStorefront';
import { resolveBundlePricingForCartItems } from './bundleCartHelpers';
import {
  mapProductToCard,
  fetchProductImage,
} from './productStorefront';

const cartFetchOptions = { useStorefrontAuth: true };
const PENDING_CART_KEY = 'bw_pending_cart_item';

export function savePendingCartItem(product) {
  const productId = Number(product?.id);
  if (!productId) return;

  sessionStorage.setItem(
    PENDING_CART_KEY,
    JSON.stringify({
      id: productId,
      price: Number(product?.price ?? 0),
      quantity: Math.max(1, Number(product?.quantity ?? 1) || 1),
      name: String(product?.name ?? '').trim(),
    })
  );
}

export function getPendingCartItem() {
  try {
    const raw = sessionStorage.getItem(PENDING_CART_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = Number(parsed?.id);
    if (!id) return null;
    return {
      id,
      price: Number(parsed?.price ?? 0),
      quantity: Math.max(1, Number(parsed?.quantity ?? 1) || 1),
      name: String(parsed?.name ?? '').trim(),
    };
  } catch {
    return null;
  }
}

export function clearPendingCartItem() {
  sessionStorage.removeItem(PENDING_CART_KEY);
}

export function getLoggedInUserId() {
  const stored = StorageManager.userId();
  if (stored) {
    const num = Number(stored);
    if (Number.isFinite(num) && num > 0) return num;
  }

  const fromToken = parseUserIdFromToken(getAuthToken());
  if (fromToken) {
    StorageManager.userId(fromToken);
    return fromToken;
  }

  return null;
}

/** Resolve UserId from storage, JWT, or User API lookup by username. */
export async function ensureLoggedInUserId() {
  const existing = getLoggedInUserId();
  if (existing) return existing;

  const username = String(StorageManager.username() || '').trim();
  const token = getAuthToken();
  if (!username || !token) return null;

  try {
    const params = new URLSearchParams({
      pageIndex: '1',
      pageSize: '50',
      search: username,
      sortColumn: 'Username',
      sortBy: 'ASC',
    });
    const { data } = await apiFetch(`/User/GetAll?${params}`, cartFetchOptions);
    const items = Array.isArray(data?.data) ? data.data : [];
    const match =
      items.find(
        (u) =>
          String(u.Username ?? u.username ?? '').toLowerCase() ===
          username.toLowerCase()
      ) || items[0];
    const userId = match?.UserId ?? match?.userId;
    if (userId) {
      const num = Number(userId);
      if (Number.isFinite(num) && num > 0) {
        StorageManager.userId(num);
        return num;
      }
    }
  } catch {
    /* lookup optional */
  }

  return null;
}

export function isCartUserLoggedIn() {
  if (!getAuthToken()) return false;
  if (getLoggedInUserId()) return true;
  return !!String(StorageManager.username() || '').trim();
}

const isCartItemDeleted = (row) => {
  const deleted = row?.IsDeleted ?? row?.isDeleted;
  return deleted === true || deleted === 1 || String(deleted).toLowerCase() === 'true';
};

/** Unwrap cart payload from API response. */
export function parseCartResponse(body) {
  if (!body) return { items: [], cartStatus: 'Added' };
  const root = body?.data && typeof body.data === 'object' && !Array.isArray(body.data)
    ? body.data
    : body;
  if (!root || typeof root !== 'object') return { items: [], cartStatus: 'Added' };

  const rawItems = root.Items ?? root.items ?? [];
  const items = (Array.isArray(rawItems) ? rawItems : []).filter((row) => !isCartItemDeleted(row));

  return {
    cartId: root.CartId ?? root.cartId ?? null,
    userId: root.UserId ?? root.userId ?? null,
    cartStatus: root.CartStatus ?? root.cartStatus ?? 'Added',
    items,
  };
}

/** Map API cart line to storefront display shape. */
export async function enrichCartItem(row) {
  if (isCartItemDeleted(row)) return null;

  const cartItemId = row.CartItemId ?? row.cartItemId;
  const productId = Number(row.ProductId ?? row.productId ?? 0);
  const productName = String(row.ProductName ?? row.productName ?? '').trim();
  const quantity = Number(row.Quantity ?? row.quantity ?? 1);
  const unitPrice = Number(row.UnitPrice ?? row.unitPrice ?? 0);
  const originalUnitPrice = Number(row.OriginalUnitPrice ?? row.originalUnitPrice ?? unitPrice);
  const isBundleItem = row.IsBundleItem === true || row.IsBundleItem === 1
    || row.isBundleItem === true || row.isBundleItem === 1
    || String(row.IsBundleItem ?? row.isBundleItem ?? '').toLowerCase() === 'true';
  const bundleProductId = row.BundleProductId ?? row.bundleProductId ?? null;
  const bundleGroupId = row.BundleGroupId ?? row.bundleGroupId ?? null;
  const bundleGroupPrice = row.BundleGroupPrice ?? row.bundleGroupPrice ?? null;

  if (!productId) return null;

  const baseItem = {
    cartItemId,
    id: productId,
    name: productName || `Product #${productId}`,
    quantity,
    originalPrice: originalUnitPrice,
    isBundleItem,
    bundleProductId,
    bundleGroupId,
    bundleGroupPrice,
    price: isBundleItem ? originalUnitPrice : unitPrice,
  };

  try {
    const card = await mapProductToCard(
      { ProductId: productId, ProductName: productName, Price: baseItem.price },
      { withColors: false, withVariant: false }
    );
    return {
      ...baseItem,
      name: productName || card.name || baseItem.name,
      imageSrc: card.imageSrc || (await fetchProductImage(productId)),
      price: baseItem.price || card.price,
    };
  } catch {
    return {
      ...baseItem,
      imageSrc: '',
    };
  }
}

export async function fetchCartByUserId(userId) {
  const { data } = await apiFetch(`/Cart/GetCart/${userId}`, cartFetchOptions);
  const parsed = parseCartResponse(data);

  const enriched = parsed.items.length
    ? (await Promise.all(parsed.items.map(enrichCartItem))).filter(
        (item) => item?.cartItemId != null
      )
    : [];

  const items = await resolveBundlePricingForCartItems(enriched, fetchBundleRelationCount);

  return {
    cartId: parsed.cartId,
    cartStatus: parsed.cartStatus,
    items,
  };
}

export async function addToCartApi({ userId, productId, quantity = 1, unitPrice, createdBy }) {
  await apiFetch('/Cart/AddToCart', {
    method: 'POST',
    ...cartFetchOptions,
    data: {
      UserId: userId,
      ProductId: productId,
      Quantity: quantity,
      UnitPrice: unitPrice,
      CreatedBy: createdBy ?? userId ?? null,
    },
  });
}

export async function addBundleToCartApi({
  userId,
  bundleProductId,
  bundlePrice,
  products,
  createdBy,
}) {
  const items = (products ?? []).map((product) => ({
    ProductId: Number(product.id),
    OriginalUnitPrice: Number(product.price ?? 0),
  }));

  await apiFetch('/Cart/AddBundleToCart', {
    method: 'POST',
    ...cartFetchOptions,
    data: {
      UserId: userId,
      BundleProductId: bundleProductId,
      BundleGroupPrice: Number(bundlePrice ?? 0),
      Items: items,
      CreatedBy: createdBy ?? userId ?? null,
    },
  });
}

export async function updateCartItemApi(cartItemId, quantity, updatedBy) {
  await apiFetch('/Cart/UpdateItem', {
    method: 'PUT',
    ...cartFetchOptions,
    data: {
      CartItemId: cartItemId,
      Quantity: quantity,
      UpdatedBy: updatedBy ?? null,
    },
  });
}

export async function removeCartItemApi(cartItemId) {
  await apiFetch(`/Cart/RemoveItem/${cartItemId}`, {
    method: 'DELETE',
    ...cartFetchOptions,
  });
}

export async function clearCartApi(userId) {
  await apiFetch(`/Cart/ClearCart/${userId}`, {
    method: 'DELETE',
    ...cartFetchOptions,
  });
}

export async function checkoutCartApi(userId, updatedBy) {
  const { data } = await apiFetch('/Cart/Checkout', {
    method: 'POST',
    ...cartFetchOptions,
    data: {
      UserId: userId,
      UpdatedBy: updatedBy ?? userId ?? null,
    },
  });

  if (data?.success === false) {
    throw new Error(data?.message || 'Checkout failed.');
  }

  const root = data?.data ?? data ?? {};
  return {
    processedCartId: root.ProcessedCartId ?? root.processedCartId ?? null,
    cartStatus: root.CartStatus ?? root.cartStatus ?? 'Processed',
  };
}

export async function fetchCartDetailById(cartId, userId) {
  const params = userId ? `?userId=${userId}` : '';
  const { data } = await apiFetch(`/Cart/GetDetail/${cartId}${params}`, cartFetchOptions);
  if (data?.success === false) {
    throw new Error(data?.message || 'Cart not found.');
  }
  const parsed = parseCartDetailResponse(data);
  if (!parsed) {
    throw new Error('Cart not found.');
  }

  const items = await resolveBundlePricingForCartItems(parsed.items ?? [], fetchBundleRelationCount);
  return { ...parsed, items };
}

export async function confirmOrderApi({
  cartId,
  userId,
  addressId,
  subTotal,
  discountAmount = 0,
  shippingAmount = 0,
  taxAmount = 0,
  totalAmount,
  orderedBy,
}) {
  const result = await confirmOrderFromCartApi({
    CartId: cartId,
    UserId: userId,
    AddressId: addressId,
    SubTotal: subTotal,
    DiscountAmount: discountAmount,
    ShippingAmount: shippingAmount,
    TaxAmount: taxAmount,
    TotalAmount: totalAmount,
    OrderedBy: orderedBy ?? userId ?? null,
  });

  return {
    orderId: result?.OrderId ?? result?.orderId ?? null,
    orderNumber: result?.OrderNumber ?? result?.orderNumber ?? null,
    cartId: result?.CartId ?? result?.cartId ?? cartId,
    addressId: result?.AddressId ?? result?.addressId ?? addressId,
    orderStatus: result?.OrderStatus ?? result?.orderStatus ?? 'Ordered',
  };
}
